import React from 'react';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import PeopleIcon from '@mui/icons-material/People';
import ListIcon from '@mui/icons-material/List';
import logo from '../../img/logo-PCG.png';
import {
  List,
  Typography,
  Divider,
  ListItemIcon,
  ListItemText,
  Collapse,
  ListItemButton,
  Box,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { isAdministrateur, isGestionPsy } from '../auth/hasRoles';
import BusinessIcon from '@mui/icons-material/Business';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import LanguageIcon from '@mui/icons-material/Language';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import LocalActivityIcon from '@mui/icons-material/LocalActivity';
import WorkIcon from '@mui/icons-material/Work';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import BallotIcon from '@mui/icons-material/Ballot';
import RoomIcon from '@mui/icons-material/Room';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import PsychologyIcon from '@mui/icons-material/Psychology';
import BadgeIcon from '@mui/icons-material/Badge';
import HistoryIcon from '@mui/icons-material/History';
import type { ReactNode } from 'react';

interface NavItem {
  path: string;
  item: string;
}

interface NavItemWithIcon extends NavItem {
  icon: ReactNode;
}

interface NavigationProps {
  darkState: boolean;
  setZoom: (zoom: boolean) => void;
  phoneResponsive: boolean;
  setOpen: (open: boolean) => void;
}

let temporaryRoute: string | null = null;

const adminPrimaryIcons = [
  <DashboardIcon key="dashboard" fontSize="small" />,
  <AssignmentIndIcon key="assignment" fontSize="small" />,
  <ListIcon key="list" fontSize="small" />,
  <RoomIcon key="room" fontSize="small" />,
  <RestoreFromTrashIcon key="restore" fontSize="small" />,
  <PersonSearchIcon key="person-search" fontSize="small" />,
];

const specialistPrimaryIcons = [
  <PsychologyIcon key="psychology" fontSize="small" />,
  <BadgeIcon key="badge" fontSize="small" />,
];

const adminSecondaryIcons = [
  <BusinessIcon key="business" fontSize="small" />,
  <LocalActivityIcon key="activity" fontSize="small" />,
  <WorkIcon key="work" fontSize="small" />,
  <SupervisedUserCircleIcon key="supervised" fontSize="small" />,
  <LanguageIcon key="language" fontSize="small" />,
  <BallotIcon key="ballot" fontSize="small" />,
  <HowToRegIcon key="how-to-reg" fontSize="small" />,
];

function isNavActive(pathname: string, path: string): boolean {
  const segment = pathname.replace(/^\//, '').split('/')[0] ?? '';
  if (path === 'specialists') {
    return segment === 'specialists' || segment === 'socialWorkers';
  }
  return segment === path;
}

export default function Navigation({ darkState, setZoom, phoneResponsive, setOpen }: NavigationProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const [expanded, setExpanded] = React.useState(false);
  const [specialistExpanded, setSpecialistExpanded] = React.useState(false);

  const adminPrimary: NavItem[] = [
    { path: 'dashboard', item: 'Tableau de bord' },
    { path: 'specialists', item: 'Liste des spécialistes' },
    { path: 'interventions', item: "Demande d'intervention" },
    { path: 'map', item: 'Carte des spécialistes' },
    { path: 'restore', item: 'Réactiver des spécialistes' },
    { path: 'prospects', item: 'Liste des prospects' },
  ];

  const adminSecondary: NavItem[] = [
    { path: 'companies', item: 'Les entreprises' },
    { path: 'specialities', item: 'Les Spécialités cliniques' },
    { path: 'experiences', item: 'Les expériences' },
    { path: 'expertises', item: 'Les expertises' },
    { path: 'languages', item: 'Les langues' },
    { path: 'administratif-status', item: 'Les statuts' },
    { path: 'certifications', item: 'Les certifications' },
  ];

  const userPrimary: NavItem[] = [
    { path: 'dashboard', item: 'Tableau de bord' },
    { path: 'specialists', item: 'Liste des spécialistes' },
    { path: 'interventions', item: "Demande d'intervention" },
    { path: 'map', item: 'Carte des spécialistes' },
  ];

  const specialistPrimary: NavItem[] = [
    { path: 'specialists', item: 'Liste des psychologues' },
    { path: 'socialWorkers', item: 'Liste des assistantes sociales' },
  ];

  const adminManagement: NavItemWithIcon[] = [
    { path: 'users', item: 'Liste des utilisateurs', icon: <PeopleIcon fontSize="small" /> },
    {
      path: 'journalisation-connexions',
      item: 'Journalisation des connexions',
      icon: <HistoryIcon fontSize="small" />,
    },
  ];

  const handleNavigation = (e: React.MouseEvent<HTMLElement>) => {
    setZoom(false);
    temporaryRoute = e.currentTarget.title;
    if (phoneResponsive) {
      setExpanded(false);
      setOpen(false);
    }
    setTimeout(() => {
      setZoom(true);
      navigate(`/${temporaryRoute}`);
      temporaryRoute = null;
    }, 50);
  };

  const handleExpandClick = () => {
    setExpanded(!expanded);
  };

  const sectionLabelSx = {
    px: 2.5,
    py: 0.75,
    display: 'block',
    color: darkState ? 'secondary.main' : 'primary.main',
    opacity: 0.85,
  };

  const nestedItemSx = {
    pl: 3.5,
    ml: 1,
    mr: 1,
    maxWidth: 'calc(100% - 8px)',
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          gap: 1.5,
          px: 2,
          pl: 3,
          py: 1.5,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box
          component="img"
          src={logo}
          alt="Logo Pros-Consulte"
          sx={{
            width: phoneResponsive ? 44 : 52,
            height: 'auto',
            flexShrink: 0,
          }}
        />
        <Typography
          variant="h6"
          color={darkState ? 'secondary' : 'primary'}
          sx={{ fontWeight: 700, letterSpacing: '0.02em' }}
        >
          Codex
        </Typography>
      </Box>

      <List
        component="nav"
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          py: 0.5,
          scrollbarWidth: 'thin',
          '& .MuiListItemIcon-root': {
            minWidth: 36,
            color: darkState ? 'secondary.main' : 'primary.main',
            opacity: 0.9,
          },
        }}
      >
        <Typography variant="overline" sx={sectionLabelSx}>
          Partie Spécialiste
        </Typography>

        {findRightMenu(adminPrimary, userPrimary).map((text, index) => {
          const isSpecialistsItem = text.path === 'specialists';
          const active = isNavActive(location.pathname, text.path);

          return (
            <React.Fragment key={text.path}>
              <ListItemButton
                id="tab"
                title={text.path}
                selected={active && !isSpecialistsItem}
                onClick={
                  isSpecialistsItem ? () => setSpecialistExpanded(!specialistExpanded) : handleNavigation
                }
              >
                <ListItemIcon>{adminPrimaryIcons[index]}</ListItemIcon>
                <ListItemText
                  primary={text.item}
                  primaryTypographyProps={{ variant: 'body2', fontWeight: active ? 600 : 500 }}
                />
                {isSpecialistsItem && (specialistExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
              </ListItemButton>

              {isSpecialistsItem && (
                <Collapse in={specialistExpanded} timeout="auto" unmountOnExit>
                  {specialistPrimary.map((subItem, subIndex) => (
                    <ListItemButton
                      id="tab"
                      key={subItem.path}
                      title={subItem.path}
                      selected={isNavActive(location.pathname, subItem.path)}
                      onClick={handleNavigation}
                      sx={nestedItemSx}
                    >
                      <ListItemIcon>{specialistPrimaryIcons[subIndex]}</ListItemIcon>
                      <ListItemText
                        primary={subItem.item}
                        primaryTypographyProps={{ variant: 'body2', fontSize: '0.8125rem' }}
                      />
                    </ListItemButton>
                  ))}
                </Collapse>
              )}
            </React.Fragment>
          );
        })}

        {isAdministrateur() && (
          <>
            <Divider sx={{ my: 1, mx: 2 }} />
            <Typography variant="overline" sx={sectionLabelSx}>
              Partie Utilisateur
            </Typography>

            {adminManagement.map((text) => (
              <ListItemButton
                id="tab"
                key={text.path}
                title={text.path}
                selected={isNavActive(location.pathname, text.path)}
                onClick={handleNavigation}
              >
                <ListItemIcon>{text.icon}</ListItemIcon>
                <ListItemText primary={text.item} primaryTypographyProps={{ variant: 'body2' }} />
              </ListItemButton>
            ))}

            <ListItemButton onClick={handleExpandClick} selected={expanded}>
              <ListItemIcon>
                <AccountTreeIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary="Gérer les options" primaryTypographyProps={{ variant: 'body2' }} />
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </ListItemButton>

            <Collapse in={expanded} timeout="auto" unmountOnExit>
              {findRightMenu(adminSecondary).map((text, index) => (
                <ListItemButton
                  id="tab"
                  key={text.path}
                  title={text.path}
                  selected={isNavActive(location.pathname, text.path)}
                  onClick={handleNavigation}
                  sx={nestedItemSx}
                >
                  <ListItemIcon>{adminSecondaryIcons[index]}</ListItemIcon>
                  <ListItemText
                    primary={text.item}
                    primaryTypographyProps={{ variant: 'body2', fontSize: '0.8125rem' }}
                  />
                </ListItemButton>
              ))}
            </Collapse>
          </>
        )}
      </List>

      <Box
        sx={{
          px: 2,
          py: 1,
          flexShrink: 0,
          borderTop: 1,
          borderColor: 'divider',
          typography: 'caption',
          color: 'text.secondary',
          textAlign: 'center',
        }}
      >
        Codex · Pros-Consulte
      </Box>
    </Box>
  );
}

function findRightMenu(
  adminTab: NavItem[] = [],
  userTab: NavItem[] = [],
  _interventionManagerTab: NavItem[] = [],
  _specialistManagerTab: NavItem[] = [],
): NavItem[] {
  if (isGestionPsy()) {
    return adminTab;
  }
  return userTab;
}
